import React, { useEffect, useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { useUser } from '../context/UserContext';
import { getTasks, createTask, updateTask, deleteTask, getUsers, getStudents, getProspects, getLeads } from '../services/api';
import EmptyState from '../components/EmptyState';


const statusOrder = ['new', 'in_progress', 'completed', 'cancelled'];

const statusLabels: Record<string, Record<string, string>> = {
  he: { new: 'חדשה', in_progress: 'בטיפול', completed: 'הושלמה', cancelled: 'בוטלה', all: 'הכל' },
  ar: { new: 'جديدة', in_progress: 'قيد المعالجة', completed: 'مكتملة', cancelled: 'ملغاة', all: 'الكل' },
};

const priorityLabels: Record<string, Record<string, string>> = {
  he: { urgent: 'דחופה', high: 'גבוהה', normal: 'רגילה', low: 'נמוכה', all: 'הכל' },
  ar: { urgent: 'عاجلة', high: 'عالية', normal: 'عادية', low: 'منخفضة', all: 'الكل' },
};

const sourceLabels: Record<string, Record<string, string>> = {
  he: { all: 'הכל', student: 'סטודנט', prospect: 'מתעניין', lead: 'ליד', standalone: 'עצמאית' },
  ar: { all: 'الكل', student: 'طالب', prospect: 'مهتم', lead: 'عميل محتمل', standalone: 'مستقلة' },
};

const statusColors: Record<string, string> = {
  new: '#e3f2fd',
  in_progress: '#fff8e1',
  completed: '#e8f5e9',
  cancelled: '#f5f5f5',
};

const priorityRowColors: Record<string, string> = {
  urgent: '#ffebee',
  high: '#fff3e0',
  normal: '#ffffff',
  low: '#f5f5f5',
};

function isOverdue(dueDate: string, status: string): boolean {
  if (!dueDate || status === 'completed' || status === 'cancelled') return false;
  return new Date(dueDate) < new Date(new Date().toDateString());
}

export default function Tasks() {
  const { t, lang } = useLang();
  const { userName } = useUser();
  const [tasks, setTasks] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [entities, setEntities] = useState<Record<string, any>>({});
  const [viewMode, setViewMode] = useState<'table' | 'kanban'>('table');
  const [dragItem, setDragItem] = useState<string | null>(null);

  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterAssignee, setFilterAssignee] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Modals
  const [showNewModal, setShowNewModal] = useState(false);
  const [editTask, setEditTask] = useState<any | null>(null);

  // New task form
  const [newForm, setNewForm] = useState({
    title: '', description: '', assignedTo: '', ccTo: '', dueDate: '', priority: 'normal', notes: '',
  });

  const loadData = useCallback(async () => {
    try {
      const [tasksRes, usersRes, studentsRes, prospectsRes, leadsRes] = await Promise.all([
        getTasks(), getUsers(), getStudents(), getProspects(), getLeads(),
      ]);
      setTasks(tasksRes.data);
      setUsers(usersRes.data);
      const map: Record<string, any> = {};
      studentsRes.data.forEach((s: any) => { map[s.id] = { ...s, type: 'student' }; });
      prospectsRes.data.forEach((p: any) => { map[p.id] = { ...p, type: 'prospect' }; });
      leadsRes.data.forEach((l: any) => { map[l.id] = { ...l, type: 'lead' }; });
      setEntities(map);
    } catch {}
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Quick-add: open the new-task modal when navigated with ?new=1
  const location = useLocation();
  useEffect(() => {
    if (new URLSearchParams(location.search).get('new') === '1') setShowNewModal(true);
  }, [location.search]);

  const getUserName = (userId: string) => {
    const u = users.find(u => u.id === userId);
    return u ? (lang === 'ar' ? u.nameAr || u.name : u.name) : userId || '-';
  };

  const getEntityName = (entityId: string) => {
    const e = entities[entityId];
    if (!e) return entityId || '-';
    if (lang === 'ar' && e.firstNameAr) return `${e.firstNameAr} ${e.lastNameAr || ''}`.trim();
    return `${e.firstName} ${e.lastName}`.trim();
  };

  const getEntityLink = (entityType: string, entityId: string) => {
    if (!entityType || !entityId) return null;
    const pathMap: Record<string, string> = { student: '/students', prospect: '/prospects', lead: '/leads' };
    const prefix = pathMap[entityType];
    return prefix ? `${prefix}/${entityId}` : null;
  };

  // Apply filters
  const filtered = tasks.filter(task => {
    if (filterStatus !== 'all' && task.status !== filterStatus) return false;
    if (filterPriority !== 'all' && task.priority !== filterPriority) return false;
    if (filterAssignee !== 'all' && task.assignedTo !== filterAssignee) return false;
    if (filterSource !== 'all') {
      if (filterSource === 'standalone') {
        if (task.relatedEntityType) return false;
      } else {
        if (task.relatedEntityType !== filterSource) return false;
      }
    }
    if (searchText && !task.title?.toLowerCase().includes(searchText.toLowerCase())) return false;
    if (dateFrom && task.dueDate && task.dueDate < dateFrom) return false;
    if (dateTo && task.dueDate && task.dueDate > dateTo) return false;
    return true;
  });

  // Summary stats
  const newCount = tasks.filter(t => t.status === 'new').length;
  const inProgressCount = tasks.filter(t => t.status === 'in_progress').length;
  const thisMonth = new Date().toISOString().slice(0, 7);
  const completedThisMonth = tasks.filter(t => t.status === 'completed' && t.createdAt?.startsWith(thisMonth)).length;
  const overdueCount = tasks.filter(t => isOverdue(t.dueDate, t.status)).length;

  // Handlers
  const handleCreateTask = async () => {
    await createTask({
      ...newForm,
      taskType: 'standalone',
      relatedEvent: '',
      relatedDocument: '',
      relatedEntityType: '',
      relatedEntityId: '',
      createdBy: userName,
      status: 'new',
    });
    setShowNewModal(false);
    setNewForm({ title: '', description: '', assignedTo: '', ccTo: '', dueDate: '', priority: 'normal', notes: '' });
    loadData();
  };

  const handleUpdateTask = async () => {
    if (!editTask) return;
    await updateTask(editTask.id, editTask);
    setEditTask(null);
    loadData();
  };

  const handleDeleteTask = async (id: string) => {
    if (window.confirm(lang === 'ar' ? 'هل تريد حذف هذه المهمة؟' : 'האם למחוק משימה זו?')) {
      await deleteTask(id);
      loadData();
    }
  };

  const handleDrop = async (newStatus: string) => {
    if (!dragItem) return;
    await updateTask(dragItem, { status: newStatus });
    setDragItem(null);
    loadData();
  };

  // Export helpers
  const exportExcel = () => {
    const headers = ['כותרת', 'מקור', 'סוג', 'מבצע', 'לידיעה', 'תאריך יעד', 'עדיפות', 'סטטוס', 'נוצר על ידי'];
    const rows = filtered.map(t => [
      t.title,
      t.relatedEntityId ? getEntityName(t.relatedEntityId) : 'עצמאית',
      t.taskType || '-',
      getUserName(t.assignedTo),
      getUserName(t.ccTo),
      t.dueDate || '-',
      priorityLabels.he[t.priority] || t.priority,
      statusLabels.he[t.status] || t.status,
      t.createdBy || '-',
    ]);
    const bom = '\uFEFF';
    const csv = bom + [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'tasks.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    const printContent = `
      <html dir="rtl"><head><meta charset="utf-8"><title>משימות</title>
      <style>body{font-family:Arial,sans-serif;direction:rtl}table{width:100%;border-collapse:collapse;font-size:12px}
      th,td{border:1px solid #ddd;padding:8px;text-align:right}th{background:#7B1FA2;color:white}
      tr.overdue{background:#ffebee}h1{color:#7B1FA2;text-align:center}</style></head><body>
      <h1>דוח משימות - מכללת מנארה</h1>
      <table><thead><tr><th>כותרת</th><th>מקור</th><th>מבצע</th><th>תאריך יעד</th><th>עדיפות</th><th>סטטוס</th></tr></thead><tbody>
      ${filtered.map(t => `<tr class="${isOverdue(t.dueDate, t.status) ? 'overdue' : ''}">
        <td>${t.title}</td><td>${t.relatedEntityId ? getEntityName(t.relatedEntityId) : 'עצמאית'}</td>
        <td>${getUserName(t.assignedTo)}</td><td>${t.dueDate || '-'}</td>
        <td>${priorityLabels.he[t.priority] || t.priority}</td><td>${statusLabels.he[t.status] || t.status}</td></tr>`).join('')}
      </tbody></table></body></html>`;
    const win = window.open('', '_blank');
    if (win) { win.document.write(printContent); win.document.close(); win.print(); }
  };

  const slang = lang === 'ar' ? 'ar' : 'he';

  const taskTypeLabel = (t: any) => {
    if (t.taskType === 'document_based') return lang === 'ar' ? 'مستند' : 'מסמך';
    if (t.taskType === 'event_based') return lang === 'ar' ? 'حدث' : 'אירוע';
    return lang === 'ar' ? 'مستقلة' : 'עצמאית';
  };

  return (
    <div>
      <div className="page-title">
        <span className="page-title-text">📋 {lang === 'ar' ? 'إدارة المهام' : 'ניהול משימות'}</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline" onClick={exportExcel}>📥 {t('exportExcel')}</button>
          <button className="btn btn-outline" onClick={exportPDF}>📄 {t('exportPDF')}</button>
          <button className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setViewMode('table')}>
            {lang === 'ar' ? 'جدول' : 'טבלה'}
          </button>
          <button className={`btn ${viewMode === 'kanban' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setViewMode('kanban')}>
            {lang === 'ar' ? 'كانبان' : 'קנבן'}
          </button>
          <button className="btn btn-secondary" onClick={() => setShowNewModal(true)}>
            + {lang === 'ar' ? 'مهمة جديدة' : 'משימה חדשה'}
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="icon">🆕</div>
          <div className="value">{newCount}</div>
          <div className="label">{statusLabels[slang].new}</div>
        </div>
        <div className="stat-card">
          <div className="icon">🔄</div>
          <div className="value">{inProgressCount}</div>
          <div className="label">{statusLabels[slang].in_progress}</div>
        </div>
        <div className="stat-card">
          <div className="icon">✅</div>
          <div className="value">{completedThisMonth}</div>
          <div className="label">{lang === 'ar' ? 'مكتملة هذا الشهر' : 'הושלמו החודש'}</div>
        </div>
        <div className="stat-card" style={{ borderRightColor: overdueCount > 0 ? '#dc3545' : undefined }}>
          <div className="icon">⚠️</div>
          <div className="value" style={{ color: overdueCount > 0 ? '#dc3545' : undefined }}>{overdueCount}</div>
          <div className="label" style={{ color: overdueCount > 0 ? '#dc3545' : undefined }}>
            {lang === 'ar' ? 'متأخرة' : 'באיחור'}
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="page-section" style={{ marginBottom: 16, padding: 16 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <select className="form-select" style={{ width: 'auto', minWidth: 120 }}
            value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="all">{lang === 'ar' ? 'الحالة: الكل' : 'סטטוס: הכל'}</option>
            {statusOrder.map(s => (
              <option key={s} value={s}>{statusLabels[slang][s]}</option>
            ))}
          </select>
          <select className="form-select" style={{ width: 'auto', minWidth: 120 }}
            value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
            <option value="all">{lang === 'ar' ? 'الأولوية: الكل' : 'עדיפות: הכל'}</option>
            <option value="urgent">{priorityLabels[slang].urgent}</option>
            <option value="high">{priorityLabels[slang].high}</option>
            <option value="normal">{priorityLabels[slang].normal}</option>
            <option value="low">{priorityLabels[slang].low}</option>
          </select>
          <select className="form-select" style={{ width: 'auto', minWidth: 120 }}
            value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)}>
            <option value="all">{lang === 'ar' ? 'المنفذ: الكل' : 'מבצע: הכל'}</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>{lang === 'ar' ? u.nameAr || u.name : u.name}</option>
            ))}
          </select>
          <select className="form-select" style={{ width: 'auto', minWidth: 120 }}
            value={filterSource} onChange={e => setFilterSource(e.target.value)}>
            <option value="all">{lang === 'ar' ? 'المصدر: الكل' : 'מקור: הכל'}</option>
            <option value="student">{sourceLabels[slang].student}</option>
            <option value="prospect">{sourceLabels[slang].prospect}</option>
            <option value="lead">{sourceLabels[slang].lead}</option>
            <option value="standalone">{sourceLabels[slang].standalone}</option>
          </select>
          <input
            className="search-input"
            style={{ minWidth: 150, flex: 1 }}
            placeholder={lang === 'ar' ? 'بحث بالعنوان...' : 'חיפוש לפי כותרת...'}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <label style={{ fontSize: 13, whiteSpace: 'nowrap' }}>{lang === 'ar' ? 'من:' : 'מתאריך:'}</label>
            <input className="form-input" type="date" style={{ width: 'auto' }} value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <label style={{ fontSize: 13, whiteSpace: 'nowrap' }}>{lang === 'ar' ? 'إلى:' : 'עד תאריך:'}</label>
            <input className="form-input" type="date" style={{ width: 'auto' }} value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </div>
        </div>
      </div>

      {/* Views */}
      {viewMode === 'table' ? (
        <div className="page-section">
          <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>{lang === 'ar' ? 'العنوان' : 'כותרת'}</th>
                <th>{lang === 'ar' ? 'المصدر' : 'מקור'}</th>
                <th>{lang === 'ar' ? 'النوع' : 'סوג'}</th>
                <th>{lang === 'ar' ? 'المنفذ' : 'מבצע'}</th>
                <th>{lang === 'ar' ? 'لإعلام' : 'לידיעה'}</th>
                <th>{lang === 'ar' ? 'تاريخ الهدف' : 'תאריך יעד'}</th>
                <th>{lang === 'ar' ? 'الأولوية' : 'עדיפות'}</th>
                <th>{t('status')}</th>
                <th>{lang === 'ar' ? 'أنشأ بواسطة' : 'נוצר על ידי'}</th>
                <th>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={10} style={{ padding: 0 }}>
                  <EmptyState
                    icon="✅"
                    title={t('emptyTasks')}
                    actionLabel={t('tasks')}
                    onAction={() => setShowNewModal(true)}
                  />
                </td></tr>
              ) : filtered.map(task => {
                const overdue = isOverdue(task.dueDate, task.status);
                return (
                  <tr key={task.id}
                    style={{
                      background: overdue ? '#ffcdd2' : priorityRowColors[task.priority] || '#fff',
                      cursor: 'pointer',
                    }}
                    onClick={() => setEditTask({ ...task })}
                  >
                    <td style={{ fontWeight: 600 }}>
                      {overdue && <span style={{ color: '#c62828', marginLeft: 4 }}>⚠️</span>}
                      {task.title}
                    </td>
                    <td>
                      {task.relatedEntityId ? (
                        (() => {
                          const link = getEntityLink(task.relatedEntityType, task.relatedEntityId);
                          return link ? (
                            <Link to={link} onClick={e => e.stopPropagation()} style={{ color: '#7B1FA2', textDecoration: 'none', fontWeight: 500 }}>
                              {getEntityName(task.relatedEntityId)}
                            </Link>
                          ) : getEntityName(task.relatedEntityId);
                        })()
                      ) : (
                        <span style={{ color: '#999' }}>{sourceLabels[slang].standalone}</span>
                      )}
                    </td>
                    <td>{taskTypeLabel(task)}</td>
                    <td>{getUserName(task.assignedTo)}</td>
                    <td>{getUserName(task.ccTo)}</td>
                    <td style={{ color: overdue ? '#c62828' : undefined, fontWeight: overdue ? 700 : undefined }}>
                      {task.dueDate || '-'}
                    </td>
                    <td>
                      <span className={`badge ${
                        task.priority === 'urgent' ? 'badge-red' :
                        task.priority === 'high' ? 'badge-yellow' :
                        task.priority === 'low' ? 'badge-gray' : 'badge-blue'
                      }`}>
                        {priorityLabels[slang][task.priority] || task.priority}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${
                        task.status === 'completed' ? 'badge-green' :
                        task.status === 'in_progress' ? 'badge-yellow' :
                        task.status === 'cancelled' ? 'badge-gray' : 'badge-blue'
                      }`}>
                        {statusLabels[slang][task.status] || task.status}
                      </span>
                    </td>
                    <td>{task.createdBy || '-'}</td>
                    <td>
                      <button className="btn btn-sm btn-danger" onClick={e => { e.stopPropagation(); handleDeleteTask(task.id); }}>
                        {t('delete')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      ) : (
        /* Kanban view */
        <div className="kanban-board">
          {statusOrder.map(status => {
            const columnTasks = filtered.filter(t => t.status === status);
            return (
              <div
                key={status}
                className="kanban-column"
                onDragOver={e => e.preventDefault()}
                onDrop={() => handleDrop(status)}
              >
                <div className="kanban-column-header" style={{ background: statusColors[status] }}>
                  <span>{statusLabels[slang][status]}</span>
                  <span className="kanban-count">{columnTasks.length}</span>
                </div>
                {columnTasks.map(task => {
                  const overdue = isOverdue(task.dueDate, task.status);
                  return (
                    <div
                      key={task.id}
                      className="kanban-card"
                      draggable
                      onDragStart={() => setDragItem(task.id)}
                      onClick={() => setEditTask({ ...task })}
                      style={{
                        background: overdue ? '#ffcdd2' : priorityRowColors[task.priority] || '#fff',
                        borderRightColor: task.priority === 'urgent' ? '#dc3545' : task.priority === 'high' ? '#ff9800' : '#81D4FA',
                      }}
                    >
                      <div className="kanban-card-name" style={{ color: '#7B1FA2' }}>
                        {overdue && <span style={{ marginLeft: 4 }}>⚠️</span>}
                        {task.title}
                      </div>
                      {task.relatedEntityId && (
                        <div className="kanban-card-info">
                          👤 {getEntityName(task.relatedEntityId)}
                          {' '}
                          <span style={{ fontSize: 11, color: '#999' }}>({sourceLabels[slang][task.relatedEntityType] || task.relatedEntityType})</span>
                        </div>
                      )}
                      <div className="kanban-card-info">👷 {getUserName(task.assignedTo)}</div>
                      {task.dueDate && (
                        <div className="kanban-card-info" style={{ color: overdue ? '#c62828' : undefined, fontWeight: overdue ? 700 : undefined }}>
                          📅 {task.dueDate}
                        </div>
                      )}
                      <div style={{ marginTop: 6 }}>
                        <span className={`badge ${
                          task.priority === 'urgent' ? 'badge-red' :
                          task.priority === 'high' ? 'badge-yellow' :
                          task.priority === 'low' ? 'badge-gray' : 'badge-blue'
                        }`} style={{ fontSize: 11 }}>
                          {priorityLabels[slang][task.priority] || task.priority}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {/* New Task Modal */}
      {showNewModal && (
        <div className="modal-overlay" onClick={() => setShowNewModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 550 }}>
            <div className="modal-header">
              <h3 className="modal-title">📋 {lang === 'ar' ? 'مهمة جديدة' : 'משימה חדשה'}</h3>
              <button className="modal-close" onClick={() => setShowNewModal(false)}>×</button>
            </div>
            <div style={{ maxHeight: '65vh', overflowY: 'auto' }}>
              <div className="form-group">
                <label className="form-label">{lang === 'ar' ? 'العنوان' : 'כותרת'} *</label>
                <input className="form-input" value={newForm.title} onChange={e => setNewForm({ ...newForm, title: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">{lang === 'ar' ? 'الوصف' : 'תיאור'}</label>
                <textarea className="form-textarea" value={newForm.description} onChange={e => setNewForm({ ...newForm, description: e.target.value })} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{lang === 'ar' ? 'المنفذ' : 'מבצע'}</label>
                  <select className="form-select" value={newForm.assignedTo} onChange={e => setNewForm({ ...newForm, assignedTo: e.target.value })}>
                    <option value="">-- {lang === 'ar' ? 'اختر' : 'בחר'} --</option>
                    {users.map(u => <option key={u.id} value={u.id}>{lang === 'ar' ? u.nameAr || u.name : u.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">{lang === 'ar' ? 'لإعلام' : 'לידיעה'}</label>
                  <select className="form-select" value={newForm.ccTo} onChange={e => setNewForm({ ...newForm, ccTo: e.target.value })}>
                    <option value="">-- {lang === 'ar' ? 'بدون' : 'ללא'} --</option>
                    {users.map(u => <option key={u.id} value={u.id}>{lang === 'ar' ? u.nameAr || u.name : u.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{lang === 'ar' ? 'تاريخ الهدف' : 'תאריך יעד'}</label>
                  <input className="form-input" type="date" value={newForm.dueDate} onChange={e => setNewForm({ ...newForm, dueDate: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">{lang === 'ar' ? 'الأولوية' : 'עדיפות'}</label>
                  <select className="form-select" value={newForm.priority} onChange={e => setNewForm({ ...newForm, priority: e.target.value })}>
                    <option value="low">{priorityLabels[slang].low}</option>
                    <option value="normal">{priorityLabels[slang].normal}</option>
                    <option value="high">{priorityLabels[slang].high}</option>
                    <option value="urgent">{priorityLabels[slang].urgent}</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{lang === 'ar' ? 'أنشأ بواسطة' : 'נוצר על ידי'}</label>
                <div className="form-input" style={{ background: '#f8f9fa' }}>{userName}</div>
              </div>
              <div className="form-group">
                <label className="form-label">{t('notes')}</label>
                <textarea className="form-textarea" value={newForm.notes} onChange={e => setNewForm({ ...newForm, notes: e.target.value })} />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={handleCreateTask} disabled={!newForm.title}>
                📋 {lang === 'ar' ? 'إنشاء' : 'צור משימה'}
              </button>
              <button className="btn btn-outline" onClick={() => setShowNewModal(false)}>{t('cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Task Modal */}
      {editTask && (
        <div className="modal-overlay" onClick={() => setEditTask(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <div className="modal-header">
              <h3 className="modal-title">✏️ {lang === 'ar' ? 'تعديل المهمة' : 'עריכת משימה'}</h3>
              <button className="modal-close" onClick={() => setEditTask(null)}>×</button>
            </div>
            <div style={{ maxHeight: '65vh', overflowY: 'auto' }}>
              {/* Source link */}
              {editTask.relatedEntityId && (
                <div className="form-group">
                  <label className="form-label">{lang === 'ar' ? 'المصدر' : 'מקור'}</label>
                  <div className="form-input" style={{ background: '#f3e5f5', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>{editTask.relatedEntityType === 'student' ? '🎓' : editTask.relatedEntityType === 'prospect' ? '📋' : '🎯'}</span>
                    {(() => {
                      const link = getEntityLink(editTask.relatedEntityType, editTask.relatedEntityId);
                      return link ? (
                        <Link to={link} style={{ color: '#7B1FA2', fontWeight: 500 }} onClick={() => setEditTask(null)}>
                          {getEntityName(editTask.relatedEntityId)} ({sourceLabels[slang][editTask.relatedEntityType]})
                        </Link>
                      ) : (
                        <strong style={{ color: '#7B1FA2' }}>{getEntityName(editTask.relatedEntityId)}</strong>
                      );
                    })()}
                  </div>
                </div>
              )}
              {/* Related document/event */}
              {editTask.relatedDocument && (
                <div className="form-group">
                  <label className="form-label">{lang === 'ar' ? 'المستند' : 'מסמך קשור'}</label>
                  <div className="form-input" style={{ background: '#e3f2fd' }}>📄 {editTask.relatedDocument}</div>
                </div>
              )}
              {editTask.relatedEvent && (
                <div className="form-group">
                  <label className="form-label">{lang === 'ar' ? 'الحدث' : 'אירוע קשור'}</label>
                  <div className="form-input" style={{ background: '#e8f5e9' }}>📨 {editTask.relatedEvent}</div>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">{lang === 'ar' ? 'العنوان' : 'כותרת'} *</label>
                <input className="form-input" value={editTask.title} onChange={e => setEditTask({ ...editTask, title: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">{lang === 'ar' ? 'الوصف' : 'תיאור'}</label>
                <textarea className="form-textarea" value={editTask.description || ''} onChange={e => setEditTask({ ...editTask, description: e.target.value })} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{lang === 'ar' ? 'المنفذ' : 'מבצע'}</label>
                  <select className="form-select" value={editTask.assignedTo || ''} onChange={e => setEditTask({ ...editTask, assignedTo: e.target.value })}>
                    <option value="">-- {lang === 'ar' ? 'اختر' : 'בחר'} --</option>
                    {users.map(u => <option key={u.id} value={u.id}>{lang === 'ar' ? u.nameAr || u.name : u.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">{lang === 'ar' ? 'لإعلام' : 'לידיעה'}</label>
                  <select className="form-select" value={editTask.ccTo || ''} onChange={e => setEditTask({ ...editTask, ccTo: e.target.value })}>
                    <option value="">-- {lang === 'ar' ? 'بدون' : 'ללא'} --</option>
                    {users.map(u => <option key={u.id} value={u.id}>{lang === 'ar' ? u.nameAr || u.name : u.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{lang === 'ar' ? 'تاريخ الهدف' : 'תאריך יעד'}</label>
                  <input className="form-input" type="date" value={editTask.dueDate || ''} onChange={e => setEditTask({ ...editTask, dueDate: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">{lang === 'ar' ? 'الأولوية' : 'עדיפות'}</label>
                  <select className="form-select" value={editTask.priority || 'normal'} onChange={e => setEditTask({ ...editTask, priority: e.target.value })}>
                    <option value="low">{priorityLabels[slang].low}</option>
                    <option value="normal">{priorityLabels[slang].normal}</option>
                    <option value="high">{priorityLabels[slang].high}</option>
                    <option value="urgent">{priorityLabels[slang].urgent}</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{t('status')}</label>
                <select className="form-select" value={editTask.status} onChange={e => setEditTask({ ...editTask, status: e.target.value })}>
                  {statusOrder.map(s => <option key={s} value={s}>{statusLabels[slang][s]}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{lang === 'ar' ? 'أنشأ بواسطة' : 'נוצר על ידי'}</label>
                <div className="form-input" style={{ background: '#f8f9fa' }}>{editTask.createdBy || '-'}</div>
              </div>
              <div className="form-group">
                <label className="form-label">{t('notes')}</label>
                <textarea className="form-textarea" value={editTask.notes || ''} onChange={e => setEditTask({ ...editTask, notes: e.target.value })} />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={handleUpdateTask} disabled={!editTask.title}>
                💾 {t('save')}
              </button>
              <button className="btn btn-danger" onClick={() => { handleDeleteTask(editTask.id); setEditTask(null); }}>
                🗑️ {t('delete')}
              </button>
              <button className="btn btn-outline" onClick={() => setEditTask(null)}>{t('cancel')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
